// src/components/AuthoritarianPlaybook.jsx
import React, { useState } from 'react';
import { Book, ChevronDown, ChevronUp, AlertOctagon, Share2, ExternalLink, Info, Link as LinkIcon } from 'lucide-react';
import CardContainer from './CardContainer';

const AuthoritarianPlaybookSection = ({ 
  quoteId, 
  tactic = null, 
  allTactics = [], 
  defaultExpanded = false,
  embedded = false // Flag to indicate if this component is embedded in EnhancedQuoteCard
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  
  // Get the current tactic and its step number
  const currentTactic = tactic || (allTactics.length > 0 ? allTactics[0] : null);
  const currentStepNumber = currentTactic?.playbook_step_number || 0;
  
  // Filter other tactics to show the complete playbook
  const otherTactics = allTactics
    .filter(t => !tactic || t.id !== tactic.id)
    .sort((a, b) => a.playbook_step_number - b.playbook_step_number);
  
  // Check if we have tactics in the allTactics array first
  if (allTactics.length === 0) {
    return null;
  }

  // Header for standalone mode
  const header = !embedded ? (
    <div className="flex items-center">
      <Book className="mr-2" size={20} />
      <h2 className="text-lg font-semibold">The Authoritarian Playbook</h2>
    </div>
  ) : null;

  // Footer for standalone mode
  const footer = !embedded ? (
    <div className="flex justify-between items-center">
      <a 
        href="https://protectdemocracy.org/project/authoritarian-playbook/" 
        target="_blank" 
        rel="noopener noreferrer" 
        className="text-purple-700 text-sm font-medium hover:underline flex items-center"
      >
        <ExternalLink size={14} className="mr-1" />
        Explore the full Authoritarian Playbook
      </a>
      
      {quoteId && (
        <button className="flex items-center text-sm bg-purple-100 text-purple-700 hover:bg-purple-200 px-3 py-1.5 rounded-md">
          <Share2 size={14} className="mr-1" />
          Share Analysis
        </button>
      )}
    </div>
  ) : null;
  
  // Content for both standalone and embedded modes
  const content = (
    <>
      {/* Introduction Section */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-start">
          <div className="flex-shrink-0 mr-3">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <AlertOctagon size={20} className="text-purple-700" />
            </div>
          </div>
          <div>
            <h3 className="font-medium text-gray-900 mb-1">How This Claim Fits Into the Larger Pattern</h3>
            <p className="text-gray-700">
              {currentTactic ? (
                <>
                  This claim is part of a coordinated effort to <strong>{currentTactic.name.toLowerCase()}</strong>, which is Step {currentStepNumber} in the authoritarian playbook. {currentTactic.justification && currentTactic.justification !== "Information about authoritarian tactics related to this quote is pending additional analysis." ? currentTactic.justification : currentTactic.description}
                </>
              ) : (
                'This claim fits into common patterns used to erode democratic norms and institutions.'
              )}
            </p>
            {currentTactic?.source_url && (
              <div className="mt-2">
                <a 
                  href={currentTactic.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline flex items-center"
                >
                  <LinkIcon size={12} className="mr-1" />
                  <span>Source: {currentTactic.source_name || "Authoritarian Playbook Research"}</span>
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Expanded Content - steps in the playbook */}
      {expanded && (
        <>
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-medium text-gray-900 mb-3">Steps in the Authoritarian Playbook</h3>
            
            <div className="space-y-4">
              {/* All steps with current one highlighted */}
              {[...allTactics]
                .sort((a, b) => a.playbook_step_number - b.playbook_step_number)
                .map((step, index) => {
                  const isCurrent = currentTactic && step.id === currentTactic.id;
                  return (
                    <div key={step.id} className="relative pl-8 pb-4">
                      <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-purple-300"></div>
                      <div className={`absolute left-1.5 top-0 w-4 h-4 rounded-full ${isCurrent ? 'bg-purple-600' : 'bg-purple-300'} border-2 border-white`}></div>
                      
                      <div className={`${isCurrent ? 'bg-purple-50 border-l-4 border-purple-600' : 'bg-white border'} p-3 rounded-r-md`}>
                        <div className="flex justify-between items-start">
                          <h4 className={`font-medium ${isCurrent ? 'text-purple-800' : 'text-gray-800'}`}>
                            Step {step.playbook_step_number}: {step.name}
                          </h4>
                          {step.source_url && (
                            <a 
                              href={step.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-purple-600 hover:underline"
                            >
                              <ExternalLink size={10} />
                            </a>
                          )}
                        </div>
                        <p className="text-sm mt-1">{step.description}</p>
                        {isCurrent && (
                          <div className="mt-2 text-xs text-purple-700 flex items-center">
                            <Info size={12} className="mr-1" />
                            This claim directly employs this tactic
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
          
          {/* Educational Content */}
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-medium text-gray-900 mb-3">Why This Matters</h3>
            <p className="text-sm text-gray-700 mb-4">
              Recognizing these patterns is crucial because authoritarian movements follow predictable steps to dismantle democracy. By understanding these tactics, citizens can better defend democratic institutions.
            </p>
            
            <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
              <h4 className="font-medium text-blue-800 mb-2">How to Protect Democracy</h4>
              <ul className="text-sm text-gray-700 space-y-2">
                <li className="flex items-start">
                  <span className="inline-block bg-blue-200 text-blue-800 rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">1</span>
                  <span><strong>Verify claims</strong> with multiple trustworthy sources before sharing</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block bg-blue-200 text-blue-800 rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">2</span>
                  <span><strong>Support independent journalism</strong> that holds power accountable</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block bg-blue-200 text-blue-800 rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">3</span>
                  <span><strong>Engage in civil dialogue</strong> with those who have different viewpoints</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block bg-blue-200 text-blue-800 rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">4</span>
                  <span><strong>Advocate for democratic reforms</strong> that strengthen institutions</span>
                </li>
              </ul>
            </div>
          </div>


          {/* Data Section */}
          <div className="p-4">
            <h3 className="font-medium text-gray-900 mb-3">Research Sources</h3>
            <div className="bg-purple-50 border border-purple-200 rounded-md p-3">
              <ul className="text-xs text-gray-700 space-y-1">
                <li className="flex items-start">
                  <span className="text-purple-600 mr-1">•</span>
                  <span>
                    <strong>Primary Source:</strong> Protect Democracy's Authoritarian Playbook
                    <a 
                      href="https://protectdemocracy.org/project/authoritarian-playbook/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="ml-1 text-purple-700 hover:underline"
                    >
                      [link]
                    </a>
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-1">•</span>
                  <span>
                    <strong>Supporting Research:</strong> Freedom House "Freedom in the World" Index
                    <a 
                      href="https://freedomhouse.org/report/freedom-world" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="ml-1 text-purple-700 hover:underline"
                    >
                      [link]
                    </a>
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-1">•</span>
                  <span>
                    <strong>Academic Analysis:</strong> V-Dem Institute's Democracy Report
                    <a 
                      href="https://www.v-dem.net/democracy_reports.html" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="ml-1 text-purple-700 hover:underline"
                    >
                      [link]
                    </a>
                  </span>
                </li>
                <li className="text-gray-500 text-right pt-1">
                  Analysis as of: {new Date().toLocaleDateString()}
                </li>
              </ul>
            </div>
          </div>
        </>
      )}
    </>
  );
  
  // If embedded in EnhancedQuoteCard, return just the content
  if (embedded) {
    return content;
  }

  // Use CardContainer for standalone mode
  return (
    <CardContainer
      header={header}
      footer={footer}
      defaultExpanded={defaultExpanded}
      expandable={true}
      className="max-w-2xl mx-auto mt-6 bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200"
    >
      {content}
    </CardContainer>
  );
};

export default AuthoritarianPlaybookSection;